import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import api from "./api";
import {
  broadcastSessionEvent,
  loginPathWithNotice,
  subscribeSessionEvents,
} from "./sessionSync";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const returnToLogin = useCallback((noticeCode = "") => {
    setUser(null);
    if (window.location.pathname !== "/login")
      window.location.assign(loginPathWithNotice(noticeCode));
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(
    () =>
      subscribeSessionEvents((event) => {
        const currentUser = userRef.current;
        if (!currentUser || (event.userId && event.userId !== currentUser.id))
          return;
        if (event.type === "inactivity") {
          returnToLogin("inactivity");
        }
        if (event.type === "logout") {
          returnToLogin("other-tab");
        }
      }),
    [returnToLogin],
  );

  // The session lives in an HttpOnly cookie set by the backend; nothing is stored in JS.
  const completeLogin = (_token, u) => setUser(u);

  const logout = async () => {
    const userId = userRef.current?.id;
    try {
      await api.post("/auth/logout");
    } catch {
      // Ignore: the cookie is cleared client-side by navigating to /login either way.
    }
    broadcastSessionEvent({ type: "logout", userId });
    returnToLogin();
  };

  const endForMaintenance = async () => {
    const userId = userRef.current?.id;
    try {
      await api.post("/auth/logout");
    } catch {
      // The maintenance middleware may already have ended the session.
    }
    broadcastSessionEvent({ type: "logout", userId });
    setUser(null);
    if (window.location.pathname !== "/maintenance") {
      window.location.assign("/maintenance");
    }
  };

  const expireForInactivity = async () => {
    const userId = userRef.current?.id;
    try {
      await api.post(
        "/auth/inactivity-timeout",
        {},
        { __sessionTimeout: true },
      );
    } catch {
      // The server may already have rejected this stale session, which is the safe outcome.
    }
    broadcastSessionEvent({ type: "inactivity", userId });
    returnToLogin("inactivity");
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        completeLogin,
        logout,
        endForMaintenance,
        expireForInactivity,
        setUser,
        refresh: bootstrap,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
